/**
 * AutocompleteService
 *
 * Service for fetching and searching autocomplete suggestions for the chat input.
 * Implements keyword-based weighted matching with text prefix bonus.
 */

import { API_URL, fetchWithTimeout } from '../core/api.js';

/**
 * Stopwords to filter from user input during matching.
 * These common words are excluded to improve match relevance.
 * @constant {Set<string>}
 */
const STOPWORDS = new Set([
  'a', 'an', 'the', 'my', 'your', 'our', 'i', 'we', 'me', 'us',
  'to', 'with', 'about', 'for', 'in', 'on', 'of', 'and', 'or',
  'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did',
  'that', 'this', 'it', 'they', 'them'
]);

/**
 * Shortcut trigger phrases and their curated autocomplete options.
 * When input exactly matches a trigger, these options are shown instead of keyword matching.
 * Each trigger has a highlightPrefix (text to bold in suggestions) and suggestions array.
 * @constant {Object.<string, {highlightPrefix: string, suggestions: string[]}>}
 */
const SHORTCUT_TRIGGERS = {
  "I'm feeling...": {
    highlightPrefix: "I'm feeling",
    suggestions: [
      "I'm feeling frustrated",
      "I'm feeling hurt",
      "I'm feeling misunderstood",
      "I'm feeling unappreciated",
      "I'm feeling overwhelmed"
    ]
  },
  "Discuss a situation...": {
    highlightPrefix: "Discuss",
    suggestions: [
      "Discuss a fight or argument we had",
      "Discuss something hurtful my partner said",
      "Discuss a recurring issue that keeps coming up",
      "Discuss something I did that I regret",
      "Discuss a boundary that was crossed"
    ]
  }
};

/**
 * Score how well a suggestion matches the user's input.
 *
 * Scoring:
 * - Text prefix match (suggestion starts with input): +3
 * - Exact keyword match: +1
 * - Prefix keyword match (3+ chars): +0.6
 *
 * @param {string} rawInput - User's raw input (with stopwords)
 * @param {string[]} filteredWords - User's input words (stopwords removed)
 * @param {Object} suggestion - Suggestion object with text and keywords
 * @returns {number} Score (higher = better match)
 */
function scoreSuggestion(rawInput, filteredWords, suggestion) {
  let score = 0;

  // 1. Text prefix bonus (uses full input, including stopwords)
  // This prioritizes suggestions that start with what the user typed
  const suggestionLower = suggestion.text.toLowerCase();
  const inputLower = rawInput.toLowerCase().trim();

  if (inputLower.length >= 2 && suggestionLower.startsWith(inputLower)) {
    score += 3;
  }

  // 2. Keyword matching (uses filtered words only)
  for (const inputWord of filteredWords) {
    for (const keyword of suggestion.keywords) {
      if (keyword === inputWord) {
        // Exact keyword match
        score += 1;
      } else if (keyword.startsWith(inputWord) && inputWord.length >= 3) {
        // Prefix match (user typed beginning of keyword)
        score += 0.6;
      }
    }
  }

  return score;
}

/**
 * Filter stopwords from input and split into words.
 *
 * @param {string} input - Raw user input
 * @returns {string[]} Array of non-stopword words, lowercased
 */
function getFilteredWords(input) {
  return input
    .toLowerCase()
    .split(/\s+/)
    .filter(word => word && !STOPWORDS.has(word));
}

export class AutocompleteService {
  /**
   * @param {string} apiBaseUrl - Optional API base URL (defaults to API_URL)
   */
  constructor(apiBaseUrl = API_URL) {
    this.apiBaseUrl = apiBaseUrl;
    this.suggestions = [];
    this.loaded = false;
  }

  /**
   * Fetch and cache autocomplete suggestions from the backend.
   *
   * @async
   * @returns {Promise<boolean>} True if loaded successfully
   */
  async loadSuggestions() {
    try {
      const response = await fetchWithTimeout(`${this.apiBaseUrl}/config/autocomplete-suggestions`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch autocomplete suggestions: ${response.status}`);
      }

      const data = await response.json();
      this.suggestions = data.data?.suggestions || [];
      this.loaded = true;

      console.log(`[AutocompleteService] Loaded ${this.suggestions.length} suggestions`);
      return true;
    } catch (error) {
      console.error('[AutocompleteService] Error loading suggestions:', error);
      this.suggestions = [];
      this.loaded = false;
      return false;
    }
  }

  /**
   * Get matching suggestions for user input, filtered by mode.
   *
   * @param {string} input - User's current input
   * @param {number} [limit=6] - Maximum number of results to return
   * @param {string} [currentMode='ADVICE'] - Current mode for filtering
   * @returns {Array<Object>} Sorted array of matching suggestions with scores
   */
  getSuggestions(input, limit = 6, currentMode = 'ADVICE') {
    const trimmedInput = input.trim();

    // Don't show suggestions for very short input
    if (trimmedInput.length < 2) {
      return [];
    }

    // Filter stopwords for keyword matching
    const filteredWords = getFilteredWords(trimmedInput);

    // Score all suggestions
    const scored = this.suggestions
      .map(suggestion => ({
        ...suggestion,
        score: scoreSuggestion(trimmedInput, filteredWords, suggestion)
      }))
      .filter(s => s.score > 0)
      .filter(s => {
        // ADVICE shows all suggestions; no mode (dashboard compose, where the
        // eventual session defaults to advice) behaves the same — a bare null
        // check matters because callers pass null explicitly, which would
        // bypass the parameter default and otherwise filter out everything.
        if (!currentMode || currentMode === 'ADVICE') return true;
        // Other modes only show suggestions tagged for that mode
        if (!s.mode) return false;
        return s.mode === currentMode;
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);

    return scored;
  }

  /**
   * Check if suggestions have been loaded.
   *
   * @returns {boolean} True if suggestions are loaded
   */
  isLoaded() {
    return this.loaded;
  }

  /**
   * Get the total number of suggestions available.
   *
   * @returns {number} Total suggestion count
   */
  getCount() {
    return this.suggestions.length;
  }

  /**
   * Get curated shortcut suggestions if input matches a trigger phrase.
   * Returns null if input doesn't match any trigger (caller should fall back to keyword matching).
   *
   * @param {string} input - User's current input
   * @returns {Array<Object>|null} Array of curated suggestions with highlightPrefix, or null if no trigger match
   */
  getShortcutSuggestions(input) {
    const config = SHORTCUT_TRIGGERS[input];
    if (!config) {
      return null;
    }

    // Return curated options with highlightPrefix for bolding in UI
    return config.suggestions.map((text, index) => ({
      id: `shortcut-${index}`,
      text,
      isShortcut: true,
      highlightPrefix: config.highlightPrefix
    }));
  }
}

// Export utility function for use in Input.js highlighting
export { getFilteredWords };
